import { ImageFit, Visibility } from '@bigcommerce/page-builder-sdk';

import { ProductCardProps, ProductSetProps, ProductValue } from '../types/productProps';
import { Prices, ProductElement, ProductLocales } from '../types/productDynamicData';
import { ActionTypes, ActionLabels } from '../types/actions';

import { DEFAULT_LOCALE } from './constants';

// Resolves the "was" price to strike through: prefer MSRP (retailPrice) when the merchant has set one,
// otherwise fall back to basePrice when it's higher than the effective price (i.e. the product is on sale).
const resolveOriginalPrice = (pricesNode: Prices | null | undefined, effectivePrice?: number): number | undefined => {
    if (!pricesNode) {
        return undefined;
    }

    if (
        pricesNode.retailPrice &&
        typeof pricesNode.retailPrice.value === 'number' &&
        typeof effectivePrice === 'number' &&
        pricesNode.retailPrice.value > effectivePrice
    ) {
        return pricesNode.retailPrice.value;
    }

    if (
        pricesNode.basePrice &&
        typeof pricesNode.basePrice.value === 'number' &&
        typeof effectivePrice === 'number' &&
        pricesNode.basePrice.value > effectivePrice
    ) {
        return pricesNode.basePrice.value;
    }

    return undefined;
};

// Normalize a locale subfolder to a single leading slash and no trailing slash (e.g. "de/" -> "/de").
const normalizeSubfolder = (localeSubfolder: string): string => `/${localeSubfolder.replace(/^\/+|\/+$/g, '')}`;

// On the default locale `path` is already correct (no subfolder). On other locales the correct link is the
// per-locale path from `product.locales` — this handles translated slugs, where the vanity path differs by locale
// (e.g. "/blue-shirt" vs "/de/blaues-hemd"). If `locales` is unavailable (e.g. the locale-alternates experiment is
// off, so the field resolves to null), fall back to prepending the current locale's subfolder to `path`.
const resolveLocalizedPath = (path: string, locales: ProductLocales | undefined, localeSubfolder?: string): string => {
    if (!path || !localeSubfolder) {
        return path;
    }

    const subfolder = normalizeSubfolder(localeSubfolder);

    const edges = locales && locales.edges ? locales.edges : [];
    const match = edges.find(edge => edge.node.path === subfolder || edge.node.path.startsWith(`${subfolder}/`));
    if (match) {
        return match.node.path;
    }

    // Fallback: no matching alternate — prepend the subfolder, guarding against double-prefixing.
    if (path === subfolder || path.startsWith(`${subfolder}/`)) {
        return path;
    }

    return `${subfolder}${path}`;
};

const buildProductCard = (props: ProductSetProps): ProductCardProps[] => {
    const {
        button: { buttonActionType, visibility },
        displayLocale,
        productImage,
        productRating: { starColorEmpty, starColorFilled },
    } = props;

    const localeSubfolder = props._ && props._.context ? props._.context.localeSubfolder : undefined;

    return props.product.value.reduce((acc, product: ProductValue): ProductCardProps[] => {
        const productProps = {
            actionLabel: ActionLabels[buttonActionType],
            actionLink: '#',
            buttonVisibility: visibility,
            displayLocale: displayLocale || DEFAULT_LOCALE,
            productImageFit: productImage.imageFit || ImageFit.Fit,
            starColorEmpty,
            starColorFilled,
        };

        const currencyNode = props._.data && props._.data.site && props._.data.site.currency;
        const productNode =
            props._.data &&
            props._.data.site &&
            props._.data.site.products &&
            props._.data.site.products.edges &&
            props._.data.site.products.edges.find(
                (productEdge: ProductElement): boolean => {
                    return productEdge.node.entityId === parseInt(product.productId, 10);
                }
            );

        if (productNode && productNode.node) {
            const {
                defaultImage,
                name,
                path,
                priceWithTax: priceWithTaxNode,
                priceWithoutTax: priceWithoutTaxNode,
            } = productNode.node;

            const localizedPath = resolveLocalizedPath(path, productNode.node.locales, localeSubfolder);
            const brandName = productNode.node.brand ? productNode.node.brand.name : '';
            const { summationOfRatings, numberOfReviews } = productNode.node.reviewSummary;
            const priceWithTaxObject = priceWithTaxNode && priceWithTaxNode.price;
            const priceWithoutTaxObject = priceWithoutTaxNode && priceWithoutTaxNode.price;
            const { currencyCode, value: priceWithoutTax } = priceWithoutTaxObject || {};
            const { value: priceWithTax } = priceWithTaxObject || {};
            const originalPriceWithoutTax = resolveOriginalPrice(priceWithoutTaxNode, priceWithoutTax);
            const originalPriceWithTax = resolveOriginalPrice(priceWithTaxNode, priceWithTax);
            const rating = summationOfRatings / numberOfReviews || 0;
            const productNodeImages = defaultImage || undefined;

            let { actionLink } = productProps;
            if (buttonActionType === ActionTypes.AddToCard) {
                actionLink = productNode.node.addToCartUrl;
                if (typeof priceWithoutTax !== 'number') {
                    productProps.buttonVisibility = Visibility.Hide;
                }
            } else if (buttonActionType === ActionTypes.AddToWishlist) {
                actionLink = productNode.node.addToWishlistUrl;
            } else if (buttonActionType === ActionTypes.GoToProduct) {
                actionLink = localizedPath;
            }

            acc.push({
                ...productProps,
                actionLink,
                brand: brandName,
                currencyCode,
                currencyDisplay: currencyNode,
                name,
                path: localizedPath,
                priceWithTax,
                priceWithoutTax,
                originalPriceWithTax,
                originalPriceWithoutTax,
                productImages: productNodeImages,
                rating,
                totalReviews: numberOfReviews,
            });
        }

        return acc;
    }, []);
};

export default buildProductCard;
